import { CONSORS_DECISION_TYPES, CONSORS_INSURANCE_OPTIONS, CONSORS_OFFER_DECISION_COLORS } from 'cpool-constants';
import { z } from 'zod';

import { schemaConsorsFamilyStatus } from './subscription/familyStatus.schema';
import { schemaConsorsGender } from './subscription/gender.schema';
import { schemaConsorsHousingSituation } from './subscription/housingSituation.schema';
import { schemaConsorsProfession } from './subscription/profession.schema';
import { schemaConsorsResidencePermitType } from './subscription/residencePermitType.schema';
import { schemaConsorsSubscriberEnum } from './subscription/subscriberEnum.schema';

export const schemaConsorsGetSubscription = z.object({
    _links: z.array(z.unknown()).optional(),
    pagination: z
        .object({
            number: z.number().int().optional(),
            size: z.number().int().optional(),
            totalElements: z.number().int().optional(),
            totalPages: z.number().int().optional(),
        })
        .optional(),
    subscriptionList: z
        .array(
            z.object({
                bankAccountData: z
                    .object({
                        owner: schemaConsorsSubscriberEnum.optional(),
                    })
                    .optional(),
                creditStates: z
                    .array(
                        z.object({
                            decisionType: z.nativeEnum(CONSORS_DECISION_TYPES).optional(),
                            lastChangeDateTime: z.string().optional(),
                            trafficLight: z.nativeEnum(CONSORS_OFFER_DECISION_COLORS).optional(),
                        }),
                    )
                    .optional(),
                financialFramework: z
                    .object({
                        commissionAmount: z.number().optional(),
                        commissionPercentage: z.number().optional(),
                        consolidatedAmount: z.number().optional(),
                        courtageAmount: z.number().optional(),
                        courtagePercentage: z.number().optional(),
                        creditAmount: z.number().optional(),
                        duration: z.number().int().optional(),
                        effectiveRate: z.number().optional(),
                        firstInstallmentDate: z.string().optional(),
                        freshMoney: z.number().optional(),
                        insuranceCalculations: z
                            .array(
                                z.object({
                                    insuranceOption: z.nativeEnum(CONSORS_INSURANCE_OPTIONS).optional(),
                                    totalInsuranceAmount: z.number().optional(),
                                }),
                            )
                            .optional(),
                        loanAmount: z.number().optional(),
                        monthlyRate: z.number().optional(),
                        nominalAmount: z.number().optional(),
                        nominalRate: z.number().optional(),
                        totalInterest: z.number().optional(),
                    })
                    .optional(),
                notes: z
                    .array(
                        z.object({
                            author: z.string().optional(),
                            comment: z.string().optional(),
                            creationDate: z.string().optional(),
                        }),
                    )
                    .optional(),
                partnerReference: z.string().optional(),
                subscriberDatas: z
                    .array(
                        z.object({
                            birthName: z.string().optional(),
                            contactAddressData: z
                                .object({
                                    city: z.string().optional(),
                                    country: z.string().optional(),
                                    email: z.string().email().optional(),
                                    street: z.string().optional(),
                                    telephoneLandline: z.string().optional(),
                                    telephoneMobile: z.string().optional(),
                                    validFrom: z.string().optional(),
                                    zipcode: z.string().optional(),
                                })
                                .optional(),
                            dateOfBirth: z.string().optional(),
                            employmentDetailsData: z
                                .object({
                                    employerAddressData: z
                                        .object({
                                            employerCity: z.string().optional(),
                                            employerCountry: z.string().optional(),
                                            employerName: z.string().optional(),
                                        })
                                        .optional(),
                                    profession: schemaConsorsProfession.optional(),
                                    professionBeginDate: z.string().optional(),
                                })
                                .optional(),
                            expenseData: z
                                .object({
                                    hasResidentialProperty: z.boolean().optional(),
                                    warmRent: z.number().optional(),
                                })
                                .optional(),
                            familySituation: schemaConsorsFamilyStatus.optional(),
                            firstName: z.string().optional(),
                            gender: schemaConsorsGender.optional(),
                            germanTaxClass: z.number().int().min(1).max(6).optional(),
                            housingSituation: schemaConsorsHousingSituation.optional(),
                            identityData: z
                                .object({
                                    residencePermitType: schemaConsorsResidencePermitType.optional(),
                                })
                                .optional(),
                            incomeData: z
                                .object({
                                    isChildBenefitInSalery: z.boolean().optional(),
                                    netIncome: z.number().optional(),
                                })
                                .optional(),
                            lastName: z.string().optional(),
                            nationality: z.string().optional(),
                            numberOfChildren: z.number().int().optional(),
                            placeOfBirth: z.string().optional(),
                            rolePlaying: schemaConsorsSubscriberEnum.optional(),
                        }),
                    )
                    .optional(),
                subscriptionInfos: z
                    .object({
                        contractIdentifier: z.string().optional(),
                        lastModificationRequestDate: z.string().optional(),
                        nextContractIdentifiers: z.array(z.number().int()).optional(),
                        previousContractIdentifier: z.string().optional(),
                    })
                    .optional(),
                supportingDocumentsRequired: z.array(z.number().int()).optional(),
            }),
        )
        .optional(),
});

export type SchemaConsorsGetSubscription = z.infer<typeof schemaConsorsGetSubscription>;
